import type { SessionConversation } from '@heidi/evidence/contracts';

/**
 * Converts the current chat conversation into a JSON string for content_snapshot
 * This captures the conversation state up to a specific position (before the response being rated)
 */
export const createChatSnapshot = (
  conversation: SessionConversation,
  upToPosition?: number,
  mode?: string
): string => {
  try {
    // Filter timeline to only include entries up to the specified position
    // If no position is specified, include all entries
    const filteredTimeline =
      upToPosition !== undefined
        ? conversation.timeline.filter((entry) => entry.position <= upToPosition)
        : conversation.timeline;

    // Create a clean snapshot of the conversation
    const snapshot = {
      timeline: filteredTimeline.map((entry) => ({
        type: entry.type,
        id: entry.id,
        position: entry.position,
        // Include content based on entry type
        ...(entry.type === 'user_message' && { content: entry.content }),
        ...(entry.type === 'text_item' && {
          content: entry.content,
          annotations: entry.annotations,
        }),
        ...(entry.type === 'reasoning_item' && {
          content: Array.isArray(entry.content) ? entry.content.join('') : entry.content,
        }),
        ...(entry.type === 'web_search_item' && {
          query: entry.query,
          sources: entry.sources,
        }),
        // Include timestamp for user messages
        ...(entry.type === 'user_message' && { timestamp: entry.timestamp }),
      })),
      metadata: {
        lastActivity: conversation.metadata.lastActivity,
        // Add current timestamp for snapshot
        snapshotTimestamp: Date.now(),
        // Include chat mode if provided
        ...(mode && { mode }),
      },
      // Include streaming state if active
      streamingState: conversation.streamingState
        ? {
            basePosition: conversation.streamingState.basePosition,
            currentActiveIndex: conversation.streamingState.currentActiveIndex,
            // Convert Map to object for JSON serialization
            activeIndices: Object.fromEntries(conversation.streamingState.activeIndices),
          }
        : null,
      // Add summary information based on filtered timeline
      summary: {
        totalEntries: filteredTimeline.length,
        userMessages: filteredTimeline.filter((entry) => entry.type === 'user_message')
          .length,
        assistantResponses: filteredTimeline.filter((entry) => entry.type === 'text_item')
          .length,
        reasoningBlocks: filteredTimeline.filter(
          (entry) => entry.type === 'reasoning_item'
        ).length,
        searchBlocks: filteredTimeline.filter((entry) => entry.type === 'web_search_item')
          .length,
      },
    };

    return JSON.stringify(snapshot, null, 2);
  } catch (error) {
    console.error('Failed to create chat snapshot:', error);
    return JSON.stringify({ error: 'Failed to create snapshot', timestamp: Date.now() });
  }
};
